import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Button,
    Paper,
    Divider,
    Alert,
    FormGroup,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Helper } from '../../../tools/Helper';
import { api_Routes } from '../../../api_Routes';

const AccountSetting = () => {
    const [preferences, setPreferences] = useState({
        notify_by_email: true,
        notify_by_push: true,
        // notification_preferences: {
        //     email: {},
        //     push: {},
        //     categories: {}
        // },
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [expanded, setExpanded] = useState(false);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    useEffect(() => {
        fetchPreferences();
    }, []);

    const fetchPreferences = async () => {
        try {
            const { response } = await Helper.Get({
                url: `${api_Routes.User.NotificationPreferences}`,
                hasToken: true,
            });
            if (response) {
                // Ensure notification_preferences has proper structure
                // const normalizedPrefs = {
                //     ...response.data,
                //     notification_preferences: response.notification_preferences || {
                //         email: {},
                //         push: {},
                //         categories: {}
                //     }
                // };
                setPreferences(response.data);
            }
        } catch (error) {
            console.error('Error fetching notification preferences:', error);
            setMessage({ type: 'error', text: 'Failed to load preferences' });
        }
    };

    const handleGlobalToggle = (key) => (event) => {
        setPreferences({
            ...preferences,
            [key]: event.target.checked,
        });
    };

    const handleCategoryToggle = (channel, category, type) => (event) => {
        const newPreferences = { ...preferences };

        // Initialize nested objects if they don't exist
        if (!newPreferences.notification_preferences) {
            newPreferences.notification_preferences = {
                email: {},
                push: {},
                categories: {}
            };
        }

        if (!newPreferences.notification_preferences[channel]) {
            newPreferences.notification_preferences[channel] = {};
        }

        if (!newPreferences.notification_preferences[channel][category]) {
            newPreferences.notification_preferences[channel][category] = {};
        }

        // Update the specific preference
        newPreferences.notification_preferences[channel][category][type] = event.target.checked;

        setPreferences(newPreferences);
    };

    const handleSave = async () => {
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const { response } = await Helper.Post({
                url: `${api_Routes.User.UpdateNotificationPreferences}`,
                hasToken: true,
                data: preferences,
            });

            if (response) {
                setMessage({ type: 'success', text: 'Preferences updated successfully' });
            }
        } catch (error) {
            console.error('Error updating preferences:', error);
            setMessage({ type: 'error', text: 'Failed to update preferences' });
        } finally {
            setLoading(false);
        }
    };

    const getPreferenceValue = (channel, category, type) => {
        // Check if the specific preference exists
        if (preferences.notification_preferences &&
            preferences.notification_preferences[channel] &&
            preferences.notification_preferences[channel][category] &&
            preferences.notification_preferences[channel][category][type] !== undefined) {
            return preferences.notification_preferences[channel][category][type];
        }

        // Default to global preference if specific preference not set
        return channel === 'email' ? preferences.notify_by_email : preferences.notify_by_push;
    };

    const notificationCategories = [
        {
            id: 'messages',
            title: 'Messages',
            types: [
                { id: 'new_message', label: 'New Messages' },
                { id: 'message_reply', label: 'Message Replies' },
                { id: 'group_message', label: 'Group Messages' },
            ]
        },
        {
            id: 'orders',
            title: 'Orders',
            types: [
                { id: 'order_placed', label: 'Order Placed' },
                { id: 'order_completed', label: 'Order Completed' },
                { id: 'order_cancelled', label: 'Order Cancelled' },
                { id: 'order_disputed', label: 'Order Disputed' },
            ]
        },
        {
            id: 'reviews',
            title: 'Reviews',
            types: [
                { id: 'new_review', label: 'New Reviews' },
                { id: 'review_reply', label: 'Review Replies' },
            ]
        },
        {
            id: 'account',
            title: 'Account',
            types: [
                { id: 'security_alerts', label: 'Security Alerts' },
                { id: 'balance_updates', label: 'Balance Updates' },
                { id: 'subscription_expiry', label: 'Subscription Expiry' },
            ]
        },
        {
            id: 'marketing',
            title: 'Marketing',
            types: [
                { id: 'promotions', label: 'Promotions' },
                { id: 'newsletter', label: 'Newsletter' },
                { id: 'feature_updates', label: 'Feature Updates' },
            ]
        }
    ];

    return (
        <Paper elevation={2} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
                Notification Preferences
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Customize how you receive notifications from our platform
            </Typography>

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }}>
                    {message.text}
                </Alert>
            )}

            {/* Global Settings */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Global Settings
                </Typography>
                <FormGroup row sx={{ gap: 4 }}>
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={preferences.notify_by_email}
                                    onChange={handleGlobalToggle('notify_by_email')}
                                    color="primary"
                                />
                            }
                            label="Email Notifications"
                        />
                        <Chip
                            label="Global Setting"
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, height: 20 }}
                        />
                    </Box>
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={preferences.notify_by_push}
                                    onChange={handleGlobalToggle('notify_by_push')}
                                    color="primary"
                                />
                            }
                            label="Push Notifications"
                        />
                        <Chip
                            label="Global Setting"
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, height: 20 }}
                        />
                    </Box>
                </FormGroup>
            </Box>

            {/*<Divider sx={{ my: 2 }} />*/}

            {/*/!* Category Settings *!/*/}
            {/*<Typography variant="h6" gutterBottom>*/}
            {/*    Category Settings*/}
            {/*</Typography>*/}
            {/*<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>*/}
            {/*    Customize notifications for specific types of activities*/}
            {/*</Typography>*/}

            {/*{notificationCategories.map((category) => (*/}
            {/*    <Accordion*/}
            {/*        key={category.id}*/}
            {/*        expanded={expanded === category.id}*/}
            {/*        onChange={handleAccordionChange(category.id)}*/}
            {/*        sx={{ mb: 1 }}*/}
            {/*    >*/}
            {/*        <AccordionSummary expandIcon={<ExpandMoreIcon />}>*/}
            {/*            <Typography>{category.title}</Typography>*/}
            {/*        </AccordionSummary>*/}
            {/*        <AccordionDetails>*/}
            {/*            {category.types.map((type) => (*/}
            {/*                <Box key={type.id} sx={{ mb: 2 }}>*/}
            {/*                    <Typography variant="subtitle2" gutterBottom>*/}
            {/*                        {type.label}*/}
            {/*                    </Typography>*/}
            {/*                    <Box sx={{ display: 'flex', gap: 4, pl: 2 }}>*/}
            {/*                        <FormControlLabel*/}
            {/*                            control={*/}
            {/*                                <Switch*/}
            {/*                                    checked={getPreferenceValue('email', category.id, type.id)}*/}
            {/*                                    onChange={handleCategoryToggle('email', category.id, type.id)}*/}
            {/*                                    color="primary"*/}
            {/*                                />*/}
            {/*                            }*/}
            {/*                            label="Email"*/}
            {/*                        />*/}
            {/*                        <FormControlLabel*/}
            {/*                            control={*/}
            {/*                                <Switch*/}
            {/*                                    checked={getPreferenceValue('push', category.id, type.id)}*/}
            {/*                                    onChange={handleCategoryToggle('push', category.id, type.id)}*/}
            {/*                                    color="primary"*/}
            {/*                                />*/}
            {/*                            }*/}
            {/*                            label="Push"*/}
            {/*                        />*/}
            {/*                    </Box>*/}
            {/*                </Box>*/}
            {/*            ))}*/}
            {/*        </AccordionDetails>*/}
            {/*    </Accordion>*/}
            {/*))}*/}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                >
                    {loading ? 'Saving...' : 'Save Preferences'}
                </Button>
            </Box>
        </Paper>
    );
};

export default AccountSetting;